import { useState } from 'react';
import { Card, Checkbox, Button, Collapse } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    RiskSymbolCell,
    ActionLinkCell,
    TagsCell,
    TreatmentProductsCell,
    SimpleTextCell,
    ActivityTypesCell,
} from '@client/components/ui/table/TableCells';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import externalLink from '../../../../../../img/external-link.svg';

const { Panel } = Collapse;

const TreatmentCard = ({ record, isSelected, onSelect }) => {
    const { t } = useTranslation();
    const [activeKeys, setActiveKeys] = useState([]);
    
    // Toggle all panels
    const toggleAllPanels = () => {
        if (activeKeys.length > 0) {
            // Collapse all if any are open
            setActiveKeys([]);
        } else {
            // Expand all panels including conditional ones
            const allKeys = ['product', 'delays', 'toxicity'];
            
            // Add cultures key if there are tag groups
            const uniqueItems = new Map();
            const addUnique = (id, name) => {
                if (name && !uniqueItems.has(id)) {
                    uniqueItems.set(id, name);
                }
            };
            record.treatment_cultures?.forEach((treatmentCulture) => {
                if (treatmentCulture.culture) {
                    addUnique(
                        treatmentCulture.culture.id,
                        treatmentCulture.culture.french_name
                    );
                }
                treatmentCulture.enemies?.forEach((enemy) => {
                    addUnique(enemy.id, enemy.display_name);
                });
            });
            if (uniqueItems.size > 0) {
                allKeys.push('cultures');
            }
            
            // Add allergens key if there are allergens
            const allergens = [];
            record.treatment_products?.forEach((tp) => {
                tp?.product?.allergens?.forEach((allergen) => {
                    if (allergen?.name && !allergens.find((a) => a.id === allergen.id)) {
                        allergens.push(allergen);
                    }
                });
            });
            if (allergens.length > 0) {
                allKeys.push('allergens');
            }
            
            setActiveKeys(allKeys);
        }
    };

    // Extract unique cultures and enemies
    const getExpandedRowData = () => {
        const uniqueItems = new Map();

        const addUnique = (id, name) => {
            if (name && !uniqueItems.has(id)) {
                uniqueItems.set(id, name);
            }
        };

        record.treatment_cultures?.forEach((treatmentCulture) => {
            if (treatmentCulture.culture) {
                addUnique(
                    treatmentCulture.culture.id,
                    treatmentCulture.culture.french_name
                );
            }
            treatmentCulture.enemies?.forEach((enemy) => {
                addUnique(enemy.id, enemy.display_name);
            });
        });

        return Array.from(uniqueItems.entries())
            .slice(0, 20)
            .map(([id, name]) => ({
                key: id,
                name: name,
            }));
    };

    // Extract allergens
    const getAllergens = () => {
        const allergens = [];
        record.treatment_products?.forEach((tp) => {
            tp?.product?.allergens?.forEach((allergen) => {
                if (allergen?.name && !allergens.find((a) => a.id === allergen.id)) {
                    allergens.push(allergen);
                }
            });
        });
        return allergens;
    };

    const tagGroups = getExpandedRowData();
    const allergens = getAllergens();

    return (
        <Card
            className="treatment-card mb-4 shadow-sm hover:shadow-md transition-shadow"
        >
            {/* Card Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelect(record.id, e.target.checked)}
                    />
                    <ActionLinkCell
                        href={`/traitements/details/${record.id}`}
                        target="_blank"
                        className="btn btn--primary hover:text-[#000] gap-2"
                    >
                        <img src={externalLink} alt="External link" />
                        {t('treatment-search|Traitement complet')}
                    </ActionLinkCell>
                </div>
                <Button
                    type="primary"
                    shape="circle"
                    onClick={toggleAllPanels}
                    icon={activeKeys.length > 0 ? <MinusOutlined /> : <PlusOutlined />}
                    size="small"
                    className="bg-[#A0D911] hover:bg-[#7CB305] border-none"
                    style={{ minWidth: '32px', width: '32px', height: '32px' }}
                />
            </div>

            {/* Intervention Period */}
            <div className="mb-4">
                <p className="title-underlined normal-case text-[20px]">
                    {record?.intervention_period?.display_name ||
                        record?.intervention_period?.name
                    }
                </p>
            </div>

            {/* Risk Indices and Symbols */}
            <div className="flex gap-6 mb-6 flex-col">
                {/* Health Risk Index */}
                <div className="flex items-center gap-2">
                    <span className="font-medium">IRS : {record?.health_risk_index}</span>
                    <div className="flex gap-1 items-center">
                        <RiskSymbolCell
                            symbolData={record.symbols?.health?.acute_toxicity_symbol}
                            alt="Acute toxicity"
                        />
                        <RiskSymbolCell
                            symbolData={record.symbols?.health?.long_term_effect_symbol}
                            alt="Long term effects"
                        />
                    </div>
                </div>

                {/* Environment Risk Index */}
                <div className="flex items-center gap-2">
                    <span className="font-medium">IRE : {record?.environment_risk_index}</span>
                    <div className="flex gap-1 items-center">
                        <RiskSymbolCell
                            symbolData={record.symbols?.environment?.fishes_symbol}
                            alt="Fish or daphnia"
                        />
                        <RiskSymbolCell
                            symbolData={record.symbols?.environment?.birds_symbol}
                            alt="Birds"
                        />
                        <RiskSymbolCell
                            symbolData={record.symbols?.environment?.bees_symbol}
                            alt="Bees"
                        />
                        <RiskSymbolCell
                            symbolData={record.symbols?.environment?.persistence_symbol}
                            alt="Persistence"
                        />
                        <RiskSymbolCell
                            symbolData={record.symbols?.environment?.leaching_symbol}
                            alt="Leaching potential"
                        />
                    </div>
                </div>
            </div>

            {/* Collapsible Sections */}
            <Collapse
                activeKey={activeKeys}
                onChange={setActiveKeys}
                ghost
                className="bg-transparent treatment-collapse"
                expandIconPosition="end"
            >
                {/* Product Section */}
                <Panel
                    header={
                        <div className="px-4 py-2 rounded font-medium">
                            {t('treatment-search|Produit commercial ou série')}
                        </div>
                    }
                    key="product"
                    className="mb-2"
                >
                    <div className="">
                        {record?.treatment_products?.length > 0 && (
                            <TreatmentProductsCell
                                TreatmentProducts={record?.treatment_products}
                            />
                        )}
                    </div>
                </Panel>

                {/* Cultures and Enemies Section */}
                {tagGroups.length > 0 && (
                    <Panel
                        header={
                            <div className="px-4 py-2 rounded font-medium">
                                {t('treatment-search|Ray-grass annuel')}
                            </div>
                        }
                        key="cultures"
                        className="mb-2"
                    >
                        <div className="bg-white">
                            <TagsCell
                                items={tagGroups}
                                getItemName={(item) => item.name}
                                getItemKey={(item) => item.key}
                            />
                        </div>
                    </Panel>
                )}

                {/* Allergens Section */}
                {allergens.length > 0 && (
                    <Panel
                        header={
                            <div className="px-4 py-2 rounded font-medium">
                                {t('treatment-search|Ali')}
                            </div>
                        }
                        key="allergens"
                        className="mb-2"
                    >
                        <div className="bg-white">
                            <ItemsWithPopover
                                items={allergens}
                                getItemName={(allergen) => allergen.name}
                                maxVisible={5}
                                isNested={false}
                            />
                        </div>
                    </Panel>
                )}

                {/* Delays Section */}
                <Panel
                    header={
                        <div className=" px-4 py-2 rounded font-medium">
                            {t('treatment-search|Délais')}
                        </div>
                    }
                    key="delays"
                    className="mb-2"
                >
                    <div className="bg-white space-y-4">
                        {/* Security Delays */}
                        <div>
                            <p className="text-[16px] font-bold">
                                {t('treatment-search|Delais de securite (DS) :')}
                            </p>
                            <SimpleTextCell text={record?.security_delays_combined} />
                        </div>

                        {/* DAAR */}
                        <div>
                            <p className="text-[16px] font-bold">
                                {t("treatment-search|Delais d'attente avant recolte (DAAR) :")}
                            </p>
                            <SimpleTextCell text={record?.delays_before_harvest_combined} />
                        </div>

                        {/* Activity Types */}
                        <div>
                            <p className="text-[16px] font-bold">
                                {t("treatment-search|Types d'activité :")}
                            </p>
                            <ActivityTypesCell treatmentProducts={record?.treatment_products} />
                        </div>
                    </div>
                </Panel>

                {/* Toxicity Section */}
                <Panel
                    header={
                        <div className=" px-4 py-2 rounded font-medium">
                            {t('treatment-search|Toxicité sur les espèces utiles')}
                        </div>
                    }
                    key="toxicity"
                    className="mb-2"
                >
                    <div className="bg-white">
                        <div className="space-y-4">
                            <p className="text-[16px] font-bold">{t('treatment-search|Selon l\'etiquette :')}</p>
                            {/* Add toxicity information here based on your data structure */}
                            <div className="text-sm text-gray-600">
                                {t('treatment-search|Toxicity information for useful species')}
                            </div>
                        </div>
                    </div>
                </Panel>
            </Collapse>
        </Card>
    );
};

export default TreatmentCard;